import { useTranslator } from '@bluebeam/react-i18n';
import { BannerContext } from 'components/Routes/BannerContext';
import Button from 'components/Button';
import ButtonGroupOverlay from 'components/ButtonGroupOverlay';
import DrawingsFilterPopover from 'components/DrawingsFilterPopover';
import Trash from 'components/Icons/Trash';
import SearchInput from 'components/inputs/SearchInput';
import LoadingAnimation from 'components/LoadingAnimation';
import ConfirmModal from 'components/modals/ConfirmModal';
import HasPermissions from 'components/Permissions/HasPermissions';
import AppStoreContext from 'contexts/AppStoreContext';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import Immutable from 'immutable';
import _ from 'lodash';
import * as queryString from 'query-string';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Interaction } from 'records/interactions';
import { UpdateMultipleDrawings } from 'records/interactions/UpdateMultipleDrawings';
import { ModalState } from 'records/ModalState';
import { Project } from 'records/Project';
import { Sheet } from 'records/Sheet';
import { RawSheetQuery } from 'records/SheetQuery';
import { SheetVersion } from 'records/SheetVersion';
import { deletePublishedPages } from 'services/pageService';
import * as toast from 'utils/toast';
import MemoizedGrid from './DrawingsGrid';
import NoDrawings from './NoDrawings';
import SortButton from './SortButton';

const PublishedTab = (props: RouteComponentProps): React.ReactElement => {
  const { translate } = useTranslator([
    'publishedTab',
    'buttons',
    'emptySearchResults',
    'toastText',
  ]);
  const [pagesLoaded, setPagesLoaded] = React.useState(false);
  const [sheetQueryLoaded, setSheetQueryLoaded] = React.useState(false);
  const [currentProject, setCurrentProject] = React.useState(new Project());
  const { actions, state } = React.useContext(AppStoreContext);
  const { height: bannerHeight } = React.useContext(BannerContext);
  const { history } = props;
  const {
    sheetQuery,
    interactions,
    selectedProject,
    selectedPages,
    sheets,
    queriedSheets,
  } = state;
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const {
    [FeatureFlag.EssentialsDrawingEditor]: isEssentialsDrawingEditorEnabled,
    [FeatureFlag.EssentialsDrawingEditorFilter]:
      isEssentialsDrawingEditorFilterEnabled,
    [FeatureFlag.ZipUploadsEnabled]: isZipUploadsEnabled,
  } = useFeatureFlags();

  React.useEffect(() => {
    return () => {
      actions.setSelectedPages(Immutable.Set());
      actions.clearInteractions();
    };
  }, []);

  React.useEffect(() => {
    const getProjects = async () => {
      const currentProject = await state.getProject();
      await actions.getProjectDocuments(selectedProject);
      await actions.getSheetSettings();
      await actions.getSheets();
      await actions.querySheets();

      const resetSearchQuery = sheetQuery.set('search', '');
      await actions.setSheetQuery(resetSearchQuery);

      setCurrentProject(currentProject);
      setPagesLoaded(true);
      setSheetQueryLoaded(true);
    };

    getProjects();

    return () => {
      setPagesLoaded(false);
    };
  }, []);

  const generateQueryParams = (sheetQuery: RawSheetQuery): string => {
    const params = {
      disciplines: sheetQuery.disciplines,
      recentlyIssued: sheetQuery.recentlyIssued,
      revisionComments: sheetQuery.revisionComments,
      search: sheetQuery.search,
      sortBy: sheetQuery.sortField,
      sortDirection: sheetQuery.sortDirection,
    };

    const stringified = queryString.stringify(params, {
      arrayFormat: 'separator',
      arrayFormatSeparator: '|',
    });

    return stringified;
  };

  const handleReset = (): void => {
    const resetSheetQuery = sheetQuery
      .set('search', '')
      .delete('disciplines')
      .delete('disciplineSearch')
      .delete('sheetDisciplines')
      .delete('revisionComments')
      .delete('recentlyIssued');

    actions.setSheetQuery(resetSheetQuery);
  };

  const handleSearchChange = (search: string): void => {
    const nextSheetQuery = sheetQuery.set('search', search);
    actions.setSheetQuery(nextSheetQuery);
  };

  const handleUploadDrawings = (): void => {
    history.push(`/project/${selectedProject}/drawings/upload`);
  };

  const handleFileSelect = async (): Promise<void> => {
    const fileInput = fileInputRef.current;
    const toastText = {
      drawingsUploaded: {
        title: translate('toastText:drawingsUploaded.title'),
        message: translate('toastText:drawingsUploaded.message'),
      },
    };
    if (fileInput && fileInput.files) {
      try {
        const files = [...Array.from(fileInput.files)];

        actions.uploadDocuments(selectedProject, files, toastText);
        history.push(`/project/${selectedProject}/drawings/uploads`);

        window.setTimeout(actions.getProcessingStatuses, 1000);
      } catch (e) {
        toast.error(
          translate('toastText:drawingsUploadError.message'),
          translate('toastText:drawingsUploadError.title')
        );
        console.error('Unable to upload files: ', e);
      }
    }
  };

  const handleEditMulti = React.useCallback((): void => {
    const updateMultipleDrawings = new UpdateMultipleDrawings();
    actions.createInteraction(updateMultipleDrawings);
  }, []);

  const handleDeletePages = async (): Promise<void> => {
    const { selectedProject, selectedPages, sheets } = state;
    const requests = new Map<string, string[]>();
    sheets
      .filter((p: Sheet) =>
        selectedPages.contains(p.versions.last<SheetVersion>().pageid)
      )
      .forEach((p: Sheet) => {
        const pageID = p.versions.last<SheetVersion>().pageid;
        const docID = p.versions.last<SheetVersion>().documentid;
        if (requests.has(docID)) {
          requests.get(docID)?.push(pageID);
        } else {
          requests.set(docID, [pageID]);
        }
      });

    const modalState = new ModalState({
      id: 'ConfirmSubmittalDelete',
      portal: 'main',
      open: true,
      component: ConfirmModal,
      props: {
        acceptButtonText: translate('buttons:delete'),
        acceptButtonType: 'delete',
        denyButtonText: translate('buttons:cancel'),
        headerText: translate('publishedTab:deleteDrawings'),
        content: translate('publishedTab:deleteDrawingsConfirmationContent'),
        acceptCallback: async (): Promise<void> => {
          try {
            actions.clearAllModals();
            await Promise.all(
              Array.from(requests.entries()).map((req) =>
                deletePublishedPages(selectedProject, req[0], req[1])
              )
            );
            actions.clearProcessingStatuses();
            const toastText = {
              fileUploadError: {
                title: translate('toastText:fileUploadError.title'),
                message: translate('toastText:fileUploadError.message'),
              },
              drawingsReadyForReview: {
                title: translate('toastText:drawingsReadyForReview.title'),
                message: translate('toastText:drawingsReadyForReview.message'),
              },
              fileUploadSuccess: {
                title: translate('toastText:fileUploadSuccess.title'),
                message: translate('toastText:fileUploadSuccess.message'),
              },
            };
            actions.getProcessingStatuses(toastText);
            await actions.getSheets();
            actions.setSelectedPages(Immutable.Set());
            actions.clearInteractions();
          } catch (err) {
            toast.error(
              translate('toastText:drawingsDeleteError.message'),
              translate('toastText:drawingsDeleteError.title')
            );
            console.log('unable to delete drawing', err);
          }
        },
        denyCallback: (): void => {
          actions.clearAllModals();
        },
      },
    });

    actions.createModal(modalState);
  };

  const handleSelectAllSheets = async (): Promise<void> => {
    const { queriedSheets, selectedPages, sheets } = state;

    if (selectedPages.size !== queriedSheets.size) {
      const searchedSheets = queriedSheets
        .map((id: string): Sheet => sheets.get(id, new Sheet()))
        .filter((sheet: Sheet): boolean => sheet.sheetid !== '');

      const sheetsToSelect = searchedSheets.reduce(
        (result: Immutable.Set<string>, sheet: Sheet) => {
          const { pageid } = sheet.versions.last<SheetVersion>();
          return result.add(pageid);
        },
        Immutable.Set<string>()
      );

      await actions.setSelectedPages(sheetsToSelect);
    }
  };

  const handleDeselectMulti = (): void => {
    actions.setSelectedPages(Immutable.Set());
  };

  const handleSheetClick = async (sheet: Sheet): Promise<void> => {
    if (isEssentialsDrawingEditorEnabled) {
      if (isEssentialsDrawingEditorFilterEnabled) {
        const sheetQuery = state.sheetQuery.serialize();

        const queryParams = generateQueryParams(sheetQuery);

        const url = `/project/${state.selectedProject}/drawing/${sheet.sheetid}/editor`;

        if (queryParams.length > 0) {
          history.push(`${url}?${queryParams}`);
        } else {
          history.push(url);
        }
      } else {
        history.push(
          `/project/${state.selectedProject}/drawing/${sheet.sheetid}/editor`
        );
      }
    } else {
      history.push(`/project/${selectedProject}/sheet/${sheet.sheetid}/view`);
    }
  };

  const isMultiEditing = (): boolean => {
    const hasInteraction =
      interactions.findIndex(
        (i: Interaction) => i.type === 'UpdateMultipleDrawings'
      ) > -1;
    return hasInteraction;
  };

  const accept = isZipUploadsEnabled ? '.pdf,.zip' : '.pdf';

  const searchedSheets = queriedSheets
    .map((id: string): Sheet => sheets.get(id, new Sheet()))
    .filter((sheet: Sheet): boolean => sheet.sheetid !== '');
  const searchResultsLabel =
    sheetQuery.search === ''
      ? translate('publishedTab:filtered')
      : translate('publishedTab:results');

  const numberOfColumns = 5;
  const numberOfRows = Math.ceil(searchedSheets.size / numberOfColumns);

  const sheetGrid: Sheet[][] = _.chunk(
    searchedSheets.toArray(),
    numberOfColumns
  );

  return (
    <div id="published-drawings-tab" className="flex grow">
      <HasPermissions permissions={['publishDrawings']}>
        {selectedPages.size > 0 && !isMultiEditing() ? (
          <ButtonGroupOverlay
            components={[
              <div>
                {selectedPages.size > 1
                  ? translate('publishedTab:itemsSelected', {
                      count: selectedPages.size,
                    })
                  : translate('publishedTab:itemSelected')}
              </div>,
              <div className="cursor-pointer" onClick={handleSelectAllSheets}>
                {translate('publishedTab:selectAll')}
              </div>,
              <div className="cursor-pointer" onClick={handleDeselectMulti}>
                {translate('publishedTab:deselectAll')}
              </div>,
              <div className="cursor-pointer" onClick={handleEditMulti}>
                {translate('publishedTab:edit')}
              </div>,
              <div
                className="row items-center cursor-pointer"
                onClick={handleDeletePages}
              >
                <Trash width="12px" />
                <div className="ml2">{translate('publishedTab:delete')}</div>
              </div>,
            ]}
          />
        ) : null}
      </HasPermissions>
      {pagesLoaded ? (
        <div className="column grow">
          <div className="row items-center justify-between px4 mt3 mb4">
            <div className="mr4 font-size-5 medium color-gray-9">
              {searchedSheets.size === sheets.size
                ? translate('publishedTab:allDrawings', {
                    count: sheets.size,
                  })
                : `${searchResultsLabel} (${searchedSheets.size})`}

              {searchedSheets.size !== sheets.size ? (
                <button
                  className="btn-link-primary btn-sm ml2 px2"
                  style={{
                    backgroundColor: '#fff',
                    fontSize: '1rem',
                  }}
                  onClick={handleReset}
                >
                  {translate('publishedTab:showAll')}
                </button>
              ) : null}
            </div>
            <div className="row">
              {sheetQueryLoaded ? (
                <SortButton
                  sortOptions={[
                    [
                      translate(
                        'publishedTab:sortButton.options.originalOrder'
                      ),
                      'originalOrder',
                    ],
                    [
                      translate('publishedTab:sortButton.options.discipline'),
                      'discipline',
                    ],
                    [
                      translate('publishedTab:sortButton.options.title'),
                      'title',
                    ],
                    [
                      translate('publishedTab:sortButton.options.number'),
                      'number',
                    ],
                    [
                      translate(
                        'publishedTab:sortButton.options.publishedDate'
                      ),
                      'publishedDate',
                    ],
                  ]}
                  startingSortField={sheetQuery.sortField}
                  startingSortDirection={sheetQuery.sortDirection}
                />
              ) : (
                <div />
              )}
              <DrawingsFilterPopover />
              <div>
                <SearchInput
                  id="published-drawings-tab-search"
                  placeholder={translate('publishedTab:search')}
                  value={sheetQuery.search}
                  onChange={handleSearchChange}
                />
              </div>
              <HasPermissions permissions={['publishDrawings']}>
                <div className="ml4">
                  <Button
                    disabled={
                      currentProject.permissions.activeProject === false
                    }
                    id="published-drawings-tab-upload-btn"
                    onClick={handleUploadDrawings}
                    variant="primary"
                  >
                    {translate('buttons:addDrawings')}
                  </Button>
                </div>
                <input
                  id="published-drawings-tab-upload-input"
                  type="file"
                  accept={accept}
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  hidden
                />
              </HasPermissions>
            </div>
          </div>

          {sheets.size > 0 ? (
            <div
              className="w100 oa p4"
              id="ThumbnailGrid"
              style={{
                backgroundColor: 'var(--light-grey-2)',
                borderTop: '1px solid var(--light-grey-1)',
                height: `calc(100vh - 169px - ${bannerHeight}px)`,
              }}
            >
              {searchedSheets.size > 0 ? (
                <MemoizedGrid
                  numberOfColumns={numberOfColumns}
                  numberOfRows={numberOfRows}
                  sheetGrid={sheetGrid}
                  handleSheetClick={handleSheetClick}
                />
              ) : (
                <div className="black-1 flex column items-center center columnDisplay">
                  <h3 style={{ marginTop: '1.2rem' }}>
                    {translate('emptySearchResults:search')}
                  </h3>
                  <p
                    className="mt2 font-size-5 gray-7"
                    style={{ maxWidth: '22.5rem' }}
                  >
                    {translate('emptySearchResults:searchMsg')}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <NoDrawings
              disabled={currentProject.permissions.activeProject === false}
              onUploadDrawings={handleUploadDrawings}
            />
          )}
        </div>
      ) : (
        <div className="flex grow flex-center">
          <LoadingAnimation
            pageName={translate('publishedTab:loadingDrawings')}
          />
        </div>
      )}
    </div>
  );
};

export default withRouter(PublishedTab);
